import React, { useEffect, useMemo, useRef, useState } from 'react';

type ArmsRaceSlot = {
  time: string;
  theme: string;
};

type ArmsRaceDay = {
  day: string;
  slots: ArmsRaceSlot[];
};

type NextSlot = {
  startsAt: Date;
  timeLabel: string;
  theme: string;
};

const schedule: ArmsRaceDay[] = [
  {
    day: 'Day 1',
    slots: [
      { time: '00:00:00', theme: 'Hero Advancement' },
      { time: '04:00:00', theme: 'Base Building' },
      { time: '08:00:00', theme: 'Unit Progression' },
      { time: '12:00:00', theme: 'Tech Research' },
      { time: '16:00:00', theme: 'Drone Boost' },
      { time: '20:00:00', theme: 'Hero Advancement' },
    ],
  },
  {
    day: 'Day 2',
    slots: [
      { time: '00:00:00', theme: 'Base Building' },
      { time: '04:00:00', theme: 'Unit Progression' },
      { time: '08:00:00', theme: 'Tech Research' },
      { time: '12:00:00', theme: 'Drone Boost' },
      { time: '16:00:00', theme: 'Hero Advancement' },
      { time: '20:00:00', theme: 'Base Building' },
    ],
  },
  {
    day: 'Day 3',
    slots: [
      { time: '00:00:00', theme: 'Unit Progression' },
      { time: '04:00:00', theme: 'Tech Research' },
      { time: '08:00:00', theme: 'Drone Boost' },
      { time: '12:00:00', theme: 'Hero Advancement' },
      { time: '16:00:00', theme: 'Base Building' },
      { time: '20:00:00', theme: 'Unit Progression' },
    ],
  },
  {
    day: 'Day 4',
    slots: [
      { time: '00:00:00', theme: 'Tech Research' },
      { time: '04:00:00', theme: 'Drone Boost' },
      { time: '08:00:00', theme: 'Hero Advancement' },
      { time: '12:00:00', theme: 'Base Building' },
      { time: '16:00:00', theme: 'Unit Progression' },
      { time: '20:00:00', theme: 'Tech Research' },
    ],
  },
  {
    day: 'Day 5',
    slots: [
      { time: '00:00:00', theme: 'Drone Boost' },
      { time: '04:00:00', theme: 'Hero Advancement' },
      { time: '08:00:00', theme: 'Base Building' },
      { time: '12:00:00', theme: 'Unit Progression' },
      { time: '16:00:00', theme: 'Tech Research' },
      { time: '20:00:00', theme: 'Drone Boost' },
    ],
  },
  {
    day: 'Day 6',
    slots: [
      { time: '00:00:00', theme: 'Hero Advancement' },
      { time: '04:00:00', theme: 'Base Building' },
      { time: '08:00:00', theme: 'Unit Progression' },
      { time: '12:00:00', theme: 'Tech Research' },
      { time: '16:00:00', theme: 'Drone Boost' },
      { time: '20:00:00', theme: 'Hero Advancement' },
    ],
  },
  {
    day: 'Day 7',
    slots: [
      { time: '00:00:00', theme: 'Base Building' },
      { time: '04:00:00', theme: 'Unit Progression' },
      { time: '08:00:00', theme: 'Tech Research' },
      { time: '12:00:00', theme: 'Drone Boost' },
      { time: '16:00:00', theme: 'Hero Advancement' },
      { time: '20:00:00', theme: 'Base Building' },
    ],
  },
];

const steps = [
  {
    title: 'Play the current theme',
    detail: 'Complete the active Arms Race theme to earn points.',
  },
  {
    title: 'Open the Points Chest',
    detail: 'Earn Arms Race Badges from the chest once you hit point thresholds.',
  },
  {
    title: 'Collect daily rewards',
    detail: 'Gather enough badges to claim the day’s Arms Race rewards.',
  },
  {
    title: 'Climb the rankings',
    detail: 'Keep earning points to secure better ranking rewards.',
  },
];

const ArmsRace: React.FC = () => {
  const dayRefs = useRef<HTMLDivElement[]>([]);
  const [serverTime, setServerTime] = useState<Date>(new Date());
  const [nextSlot, setNextSlot] = useState<NextSlot | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('fade-in');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2, rootMargin: '0px 0px -40px 0px' }
    );

    dayRefs.current.forEach((dayCard) => {
      if (dayCard) {
        dayCard.classList.add('fade-in-ready');
        observer.observe(dayCard);
      }
    });

    return () => {
      dayRefs.current.forEach((dayCard) => {
        if (dayCard) observer.unobserve(dayCard);
      });
    };
  }, []);

  const slotTimes = useMemo(() => ['00:00:00', '04:00:00', '08:00:00', '12:00:00', '16:00:00', '20:00:00'], []);

  const getThemeFor = (dayIndex: number, time: string): string => {
    const daySchedule = schedule[dayIndex % schedule.length];
    const found = daySchedule.slots.find((slot) => slot.time === time);
    return found ? found.theme : 'Arms Race';
  };

  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      setServerTime(now);

      // Align Day 1 to Monday (server-time assumption)
      const dayIndex = (now.getUTCDay() + 6) % 7;

      const findNextSlot = (): NextSlot => {
        for (let dayOffset = 0; dayOffset < 8; dayOffset += 1) {
          const candidateDay = new Date(now);
          candidateDay.setUTCDate(now.getUTCDate() + dayOffset);
          const candidateDayIndex = (dayIndex + dayOffset) % schedule.length;

          for (const time of slotTimes) {
            const [h, m, s] = time.split(':').map(Number);
            const slotDate = new Date(candidateDay);
            slotDate.setUTCHours(h, m, s, 0);

            if (slotDate > now) {
              return {
                startsAt: slotDate,
                timeLabel: time,
                theme: getThemeFor(candidateDayIndex, time),
              };
            }
          }
        }

        // Fallback: first slot next week
        const firstSlotTime = slotTimes[0];
        const [h, m, s] = firstSlotTime.split(':').map(Number);
        const nextWeek = new Date(now);
        nextWeek.setUTCDate(now.getUTCDate() + 7);
        nextWeek.setUTCHours(h, m, s, 0);
        return {
          startsAt: nextWeek,
          timeLabel: firstSlotTime,
          theme: getThemeFor((dayIndex + 7) % schedule.length, firstSlotTime),
        };
      };

      setNextSlot(findNextSlot());
    }, 1000);

    return () => clearInterval(interval);
  }, [slotTimes]);

  const formatTime = (date: Date): string =>
    date.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: 'UTC',
    });

  const formatCountdown = (target: Date): string => {
    const diffMs = target.getTime() - serverTime.getTime();
    if (diffMs <= 0) return 'Starting now';
    const totalSeconds = Math.floor(diffMs / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds
      .toString()
      .padStart(2, '0')}`;
  };

  return (
    <section id="arms-race" className="section section-dark">
      <div className="container">
        <h2 className="section-title">Arms Race Schedule</h2>
        <div className="flex flex-col md:flex-row items-center justify-center gap-6 text-text-secondary">
          <div className="bg-bg-card border border-border rounded-xl px-5 py-4 text-center shadow-glow">
            <div className="text-sm uppercase tracking-wide text-text-muted">Server Time</div>
            <div className="font-orbitron text-2xl text-primary-light">{formatTime(serverTime)}</div>
          </div>
          {nextSlot && (
            <div className="bg-bg-card border border-border rounded-xl px-5 py-4 text-center shadow-glow">
              <div className="text-sm uppercase tracking-wide text-text-muted">Next Theme</div>
              <div className="font-orbitron text-lg text-primary-light">
                {nextSlot.theme} @ {nextSlot.timeLabel}
              </div>
              <div className="text-text-secondary text-sm mt-1">
                Starts in {formatCountdown(nextSlot.startsAt)}
              </div>
            </div>
          )}
        </div>
        <p className="text-center text-text-secondary max-w-3xl mx-auto leading-relaxed">
          Plan your upgrades and battles around the daily Arms Race themes. Times are shown in server time.
          Each 4-hour window focuses on a specific activity to maximize points for your alliance.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-10">
          {schedule.map((day, index) => (
            <div
              key={day.day}
              className="bg-bg-card rounded-xl border border-border overflow-hidden shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-primary hover:shadow-glow"
              ref={(el) => {
                if (el) dayRefs.current[index] = el;
              }}
            >
              <div className="bg-bg-darker px-6 py-4 border-b border-border flex items-center justify-between">
                <h3 className="font-orbitron text-xl text-primary-light">{day.day}</h3>
                <span className="text-text-muted text-sm uppercase tracking-wide">Server Time</span>
              </div>
              <div className="divide-y divide-border">
                {day.slots.map((slot) => (
                  <div key={`${day.day}-${slot.time}`} className="flex items-center justify-between px-6 py-4">
                    <div className="font-orbitron text-lg text-text-primary">{slot.time}</div>
                    <div className="text-text-secondary text-lg font-semibold">{slot.theme}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12">
          <h3 className="text-center font-orbitron text-2xl text-primary-light mb-6">How to Earn Points</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {steps.map((step, idx) => (
              <div
                key={step.title}
                className="bg-bg-card rounded-xl border border-border p-5 text-center transition-all duration-300 hover:-translate-y-1 hover:border-primary hover:shadow-glow"
              >
                <div className="font-orbitron text-3xl text-primary mb-2">0{idx + 1}</div>
                <h4 className="font-orbitron text-lg text-primary-light mb-2">{step.title}</h4>
                <p className="text-text-secondary text-sm leading-relaxed">{step.detail}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ArmsRace;

